// src/components/Header.tsx
import React, { useEffect, useRef, useState } from "react";
import "./Header.css";
import { Link, NavLink, useLocation } from "react-router-dom";
import { getRoutePaths, getLangFromPath } from "../../utils/i18n";
import { useTranslations } from "../../utils/translations";
import { useLanguage } from "../../utils/LanguageContext";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import logoMark from "url:../../../assets/logo-mark.svg";

const Header = ({ scrolled = false }) => {
  const { currentLang } = useLanguage();
  const { t } = useTranslations();
  const location = useLocation();
  const pathLang = getLangFromPath(location.pathname);
  const paths = getRoutePaths(pathLang);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolledLocal, setIsScrolledLocal] = useState(false);
  const [isCondensing, setIsCondensing] = useState(false);
  const navRef = useRef(null);
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);
  const isScrolledRef = useRef(false);
  const isCondensingRef = useRef(false);
  const condenseTimerRef = useRef(null);
  const wasMenuOpenRef = useRef(false);

  const isScrolled = scrolled || isScrolledLocal;
  const navLinks = [
    { to: paths.services, label: t('header', 'navigation.services', 'services') },
    { to: paths.lab, label: t('header', 'navigation.lab', 'lab') },
    { to: paths.gallery, label: t('header', 'navigation.gallery', 'gallery') },
    { to: paths.about, label: t('header', 'navigation.about', 'about') },
    { to: paths.contact, label: t('header', 'navigation.contact', 'contact') },
  ];

  // Get header tagline from translations
  const tagline = t('header', 'tagline', 'independent / creative / technology');

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        return;
      }
      if (event.key === "Tab") {
        const focusables = [
          toggleRef.current,
          ...Array.from(navRef.current?.querySelectorAll("a, button") || []),
        ].filter(Boolean);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    const handlePointer = (event) => {
      const nav = navRef.current;
      const toggle = toggleRef.current;
      if (!nav || !toggle) return;
      if (nav.contains(event.target) || toggle.contains(event.target)) {
        return;
      }
      setMenuOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", handlePointer);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", handlePointer);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      window.requestAnimationFrame(() => {
        firstLinkRef.current?.focus();
      });
    } else if (wasMenuOpenRef.current) {
      toggleRef.current?.focus();
    }
    wasMenuOpenRef.current = menuOpen;
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const targets = Array.from(document.querySelectorAll("main.app, .footer"));
    targets.forEach((el) => {
      el.inert = true;
    });
    return () => {
      targets.forEach((el) => {
        el.inert = false;
      });
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && menuOpen) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [menuOpen]);

  useEffect(() => {
    isScrolledRef.current = isScrolledLocal;
  }, [isScrolledLocal]);

  useEffect(() => {
    isCondensingRef.current = isCondensing;
  }, [isCondensing]);

  useEffect(() => {
    const getScrollTop = () =>
      window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const thresholdDown = 60;
    const thresholdUp = 36;

    const stopCondensing = () => {
      if (condenseTimerRef.current) {
        window.clearTimeout(condenseTimerRef.current);
        condenseTimerRef.current = null;
      }
      if (isCondensingRef.current) {
        isCondensingRef.current = false;
        setIsCondensing(false);
      }
    };

    const setScrolledImmediate = (nextValue) => {
      if (isScrolledRef.current === nextValue) return;
      isScrolledRef.current = nextValue;
      setIsScrolledLocal(nextValue);
    };

    const startCondensing = () => {
      if (isScrolledRef.current || isCondensingRef.current) return;
      isCondensingRef.current = true;
      setIsCondensing(true);
      if (condenseTimerRef.current) {
        window.clearTimeout(condenseTimerRef.current);
      }
      condenseTimerRef.current = window.setTimeout(() => {
        isScrolledRef.current = true;
        setIsScrolledLocal(true);
        isCondensingRef.current = false;
        setIsCondensing(false);
        condenseTimerRef.current = null;
      }, 220);
    };

    const updateScrolled = () => {
      const scrollTop = getScrollTop();
      const isDesktop = window.innerWidth > 1024;
      const currentlyScrolled = isScrolledRef.current;
      const shouldBeScrolled = currentlyScrolled ? scrollTop > thresholdUp : scrollTop > thresholdDown;

      if (isDesktop) {
        if (shouldBeScrolled) {
          if (!currentlyScrolled) {
            startCondensing();
          }
        } else {
          stopCondensing();
          setScrolledImmediate(false);
        }
        return;
      }

      stopCondensing();
      setScrolledImmediate(shouldBeScrolled);
    };

    updateScrolled();
    window.addEventListener("scroll", updateScrolled, { passive: true });
    window.addEventListener("resize", updateScrolled);
    return () => {
      stopCondensing();
      window.removeEventListener("scroll", updateScrolled);
      window.removeEventListener("resize", updateScrolled);
    };
  }, []);

  return (
    <header className="header">
      <div className={`header-main${isScrolled ? " is-scrolled" : ""}${isCondensing ? " is-condensing" : ""}`}>
        <div className="brand-wrap">
          <Link to={paths.home} className="brand" aria-label="thewhizkind home">
            <img className="brand-logo" src={logoMark} alt="" />
            <span className="brand-wordmark">thewhizkind</span>
          </Link>
        </div>
        <nav
          className={`nav-menu${menuOpen ? " is-open" : ""} ${menuOpen ? "" : "hidden"} lg:flex`}
          aria-label="Primary"
          id="primary-navigation"
          ref={navRef}
          role={menuOpen ? "dialog" : undefined}
          aria-modal={menuOpen ? "true" : undefined}
        >
          {navLinks.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link-item${isActive ? " active" : ""}`}
              data-index={`0${index + 1}`}
              onClick={closeMenu}
              ref={index === 0 ? firstLinkRef : undefined}
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
          <div className="nav-menu-foot">
            <span>thewhizkind — index</span>
            <Link to={paths.principles} onClick={closeMenu}>
              {t('footer', 'footerLinks.principles', 'principles')}
            </Link>
          </div>
        </nav>
        <div className="nav-language-selector">
          <LanguageSelector />
        </div>
        <div className="nav-language-selector-mobile">
          <LanguageSelector />
        </div>
        <button
          type="button"
          className={`nav-toggle${menuOpen ? " is-open" : ""} lg:hidden`}
          aria-label={menuOpen ? t('header', 'menu.close', 'Close menu') : t('header', 'menu.open', 'Open menu')}
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          onClick={() => setMenuOpen((open) => !open)}
          ref={toggleRef}
        >
          <span className="nav-toggle-lines" aria-hidden="true">
            <span className="nav-toggle-line" />
            <span className="nav-toggle-line" />
            <span className="nav-toggle-line" />
          </span>
        </button>

      </div>
      <div
        className={`nav-overlay${menuOpen ? " is-open" : ""}`}
        aria-hidden="true"
        onClick={closeMenu}
      />
    </header>
  );
};

export default Header;
